import {
  Activity,
  ArrowRightLeft,
  BarChart3,
  BookOpen,
  Building2,
  CalendarDays,
  Cloud,
  Coins,
  Cpu,
  DollarSign,
  Filter,
  Gift,
  HeartPulse,
  History,
  Home,
  Key,
  Mail,
  Megaphone,
  Monitor,
  MonitorSmartphone,
  Newspaper,
  Presentation,
  ScrollText,
  Sparkles,
  Target,
  Ticket,
  TrendingUp,
  Users,
  Waypoints,
  Wrench,
} from 'lucide-react'
import type { ElementType } from 'react'

export interface NavItem {
  title: string
  href: string
  icon: ElementType
  keywords?: string[]
  separator?: boolean
}

export interface NavGroup {
  title: string
  icon: ElementType
  items: NavItem[]
}

export const homeItem: NavItem = {
  title: 'Home',
  href: '/',
  icon: Home,
  keywords: ['dashboard', 'overview', 'start'],
}

export const playbookItems: NavGroup = {
  title: 'Playbook',
  icon: BookOpen,
  items: [
    {
      title: 'Overview',
      href: '/playbook',
      icon: BookOpen,
      keywords: ['playbook', 'index', 'home'],
    },
    {
      title: 'Changelog',
      href: '/playbook/changelog',
      icon: History,
      keywords: ['updates', 'changes', 'releases', 'history'],
    },
    {
      title: 'Product',
      href: '/playbook/product/vision',
      icon: Target,
      keywords: ['vision', 'strategy', 'personas', 'roadmap', 'mission', 'goals'],
    },
    {
      title: 'Marketing',
      href: '/playbook/marketing',
      icon: Megaphone,
      keywords: ['messaging', 'brand', 'ads', 'campaigns', 'audience', 'copy'],
    },
    {
      title: 'Presentations',
      href: '/playbook/presentations',
      icon: Presentation,
      keywords: ['decks', 'slides', 'conference', 'socialkit', 'share'],
    },
    {
      title: 'Communications',
      href: '/playbook/communications/user-emails',
      icon: Mail,
      keywords: ['email', 'emails', 'intercom', 'transactional', 'lifecycle', 'nurture'],
    },
    {
      title: 'Engineering',
      href: '/playbook/engineering/getting-started/development-setup',
      icon: Wrench,
      keywords: ['setup', 'architecture', 'development', 'guides', 'technical'],
    },
    {
      title: 'Analytics',
      href: '/playbook/analytics',
      icon: BarChart3,
      keywords: ['metrics', 'data', 'dashboards', 'bigquery', 'kpi'],
    },
    {
      title: 'Monetization',
      href: '/playbook/monetization',
      icon: DollarSign,
      keywords: ['pricing', 'plans', 'revenue', 'subscriptions', 'payments'],
    },
  ],
}

export const metricsItems: NavGroup = {
  title: 'Metrics',
  icon: BarChart3,
  items: [
    {
      title: 'Business',
      href: '/metrics/business',
      icon: TrendingUp,
      keywords: ['revenue', 'mrr', 'arr', 'subscriptions', 'money'],
    },
    {
      title: 'Funnel',
      href: '/metrics/funnel',
      icon: Filter,
      keywords: ['conversion', 'acquisition', 'signup', 'trial', 'paid'],
    },
    {
      title: 'TROI',
      href: '/metrics/troi',
      icon: DollarSign,
      keywords: ['troi', 'roi', 'payback', 'marketing', 'spend', 'revenue', 'cohort', 'cac'],
    },
    {
      title: 'Campaigns',
      href: '/metrics/campaigns',
      icon: Megaphone,
      keywords: ['ads', 'marketing', 'facebook', 'meta', 'advertising', 'cac'],
    },
    {
      title: 'Engagement',
      href: '/metrics/engagement',
      icon: Activity,
      keywords: [
        'pipeline',
        'publish',
        'scheduled',
        'autopilot',
        'content',
        'features',
        'dau',
        'stickiness',
      ],
    },
    {
      title: 'Retention',
      href: '/metrics/retention',
      icon: CalendarDays,
      keywords: [
        'retention',
        'cohort',
        'churn',
        'd1',
        'd7',
        'd14',
        'd28',
        'lifecycle',
        'returning',
      ],
    },
    {
      title: 'AI Costs',
      href: '/metrics/ai-costs',
      icon: Coins,
      keywords: ['ai', 'cost', 'tokens', 'usage', 'spending', 'model'],
    },
    {
      title: 'SocialKit',
      href: '/metrics/socialkit',
      icon: Sparkles,
      keywords: [
        'socialkit',
        'engine',
        'generation',
        'fallback',
        'quality',
        'score',
        'reels',
        'carousel',
        'v2',
      ],
    },
    {
      title: 'Taka TV',
      href: '/metrics/taka-tv',
      icon: Monitor,
      keywords: ['dashboard', 'display', 'tv', 'screen'],
      separator: true,
    },
    {
      title: 'Taka News',
      href: '/metrics/taka-news',
      icon: Newspaper,
      keywords: ['updates', 'announcements', 'news', 'whatsapp'],
    },
  ],
}

export const platformItems: NavGroup = {
  title: 'Platform',
  icon: Cloud,
  items: [
    {
      title: 'Users',
      href: '/platform/users',
      icon: Users,
      keywords: ['customers', 'accounts', 'members', 'people'],
    },
    {
      title: 'Businesses',
      href: '/platform/businesses',
      icon: Building2,
      keywords: ['business', 'brand', 'workspace', 'team', 'company'],
    },
    {
      title: 'Creatives',
      href: '/platform/creatives',
      icon: Sparkles,
      keywords: ['instagram', 'email', 'flyer', 'content', 'creative'],
    },
    {
      title: 'Command Log',
      href: '/platform/command-log',
      icon: ScrollText,
      keywords: [
        'audit',
        'trail',
        'admin',
        'actions',
        'command',
        'log',
        'history',
        'suspend',
        'receipts',
      ],
      separator: true,
    },
    {
      title: 'API Keys',
      href: '/platform/api-keys',
      icon: Key,
      keywords: ['tokens', 'authentication', 'access', 'keys'],
    },
    {
      title: 'Client Policy',
      href: '/platform/client-policy',
      icon: MonitorSmartphone,
      keywords: ['app', 'mobile', 'version', 'update'],
    },
    {
      title: 'Content Engine',
      href: '/platform/content-engine',
      icon: Cpu,
      keywords: [
        'engine',
        'production',
        'benchmark',
        'benchmarks',
        'socialkit',
        'taka',
        'generate',
        'compare',
        'evals',
      ],
    },
    {
      title: 'Social Gateways',
      href: '/platform/social-gateways',
      icon: Waypoints,
      keywords: [
        'gateway',
        'gateways',
        'connector',
        'composio',
        'phyllo',
        'direct',
        'routing',
        'connect',
        'publish',
        'facebook',
        'instagram',
      ],
    },
    {
      title: 'Meta Migration',
      href: '/platform/meta-migration',
      icon: ArrowRightLeft,
      keywords: ['meta', 'composio', 'direct', 'migration', 'facebook', 'instagram', 'reconnect'],
    },
    {
      title: 'Promo codes',
      href: '/platform/promo-codes',
      icon: Ticket,
      keywords: [
        'promo',
        'code',
        'codes',
        'coupon',
        'discount',
        'offer',
        'redemption',
        'campaign',
        'influencer',
        'free',
      ],
    },
    {
      title: 'Referral Program',
      href: '/platform/referral',
      icon: Gift,
      keywords: ['referral', 'referrals', 'invite', 'refer', 'credits', 'reward', 'rewards'],
    },
    {
      title: 'System Health',
      href: '/platform/system-health',
      icon: HeartPulse,
      keywords: [
        'health',
        'freshness',
        'pipelines',
        'airbyte',
        'status',
        'data',
        'sync',
        'bigquery',
        'errors',
        'exceptions',
        'posthog',
        'canary',
      ],
      separator: true,
    },
  ],
}

export const allNavGroups: NavGroup[] = [playbookItems, metricsItems, platformItems]
